/**
 * The Agreement panel's reading (the owner, 2 Nov 2026): what api.agreement_panel answers, and the words for it. Pure: the panel and its tests share it.
 */
import { useQuery } from '@tanstack/react-query';
import { rpc } from '../../lib/supabase';

export type Every = 'DAILY' | 'ALTERNATE' | 'WEEKDAYS' | 'CYCLE';
export interface AgreementLine { variantId: string; code: string; name: string; quantity: number; every: Every; weekdays?: number[] | null; cycle?: number[] | null; nextDelivery: string | null; pricePaise: number | null }
export interface AgreementCard { subscriptionId: string; code: string; from: string; until: string | null; status: string; pausedFrom: string | null; doorId: string; lines: AgreementLine[]; nextDelivery: string | null }
export type DayState = 'OPEN' | 'PREPARED' | 'FROZEN' | 'LOADED' | 'DELIVERED' | 'PAST';
export interface RunException { kind: 'RUN'; effect: 'QUANTITY' | 'PAUSE'; variantId: string | null; variant: string | null; quantity: number | null; usual: number | null; from: string; until: string | null; ids: string[]; untilNotice: boolean; firstState: DayState; firstFrozen: boolean; reason: string | null }
export interface ExtraException { kind: 'EXTRA'; orderId: string; orderCode: string; status: string; from: string; until: string; mode: string; lines: { variant: string; quantity: number }[]; firstState: DayState }
export interface HoldException { kind: 'HOLD'; id: string; from: string; until: null; previous: { code?: string; variantId?: string; quantity: number }[]; held: { variantId?: string; quantity: number }[]; reason: string | null }
export interface StatusException { kind: 'STATUS'; id: string; status: string; from: string; until: string | null; note: string | null }
export interface NoteException { kind: 'NOTE' | 'ELSEWHERE'; changeId: string | null; from: string; until: string; note: string | null }
export interface ApprovalException { kind: 'APPROVAL'; changeId: string; change: string; from: string; until: string | null; note: string | null }
export type AgreementException = RunException | ExtraException | HoldException | StatusException | NoteException | ApprovalException;
export interface HistoryRow { at: string; kind: 'DAY' | 'AGREEMENT' | 'HOLD' | 'RESUME' | 'STATUS'; what: string; from: string | null; until: string | null; late: boolean; state: string | null; why: string | null; by: string | null }
export interface AgreementPanelRead {
  customer: { id: string; code: string; name: string; status: { status: string; from: string | null; until: string | null; reasonLabel: string | null; note: string | null } };
  door: string | null;
  doors: { id: string; code: string; label: string; usual: boolean; hasAgreement: boolean }[];
  today: string; tomorrow: string;
  cutoff: { time: string; tomorrowCutoffAt: string; tomorrowState: DayState };
  firstOpenDay: string;
  now: AgreementCard | null;
  /** The last agreement of any status, when none is live: the Rejoin form starts from it. */
  last?: AgreementCard | null;
  waiting: AgreementCard[];
  exceptions: AgreementException[];
  discounts: { id: string; variant: string | null; amountPaise: number | null; percent: number | null; from: string; until: string | null }[];
  history: HistoryRow[];
  may: { amend: boolean; days: boolean; status: boolean };
  /** Which commands the database has: the desk can go out before them. */
  commands: { amend: boolean; cancel: boolean; rejoin: boolean };
}

export const AGREEMENT_KEY = 'customer-agreement';
export function useAgreementPanel(customerId: string, door: string | null) {
  return useQuery({ queryKey: [AGREEMENT_KEY, customerId, door], retry: false,
    queryFn: () => rpc<AgreementPanelRead>('agreement_panel', { p_customer_id: customerId, p_door: door }) });
}

const DAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const d = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
export const dayWord = d;
export const daysWord = (weekdays: number[] | null | undefined): string => {
  const w = [...new Set(weekdays ?? [])].sort((a, b) => a - b);
  if (w.length === 7) return 'every day';
  if (w.length === 6) return `all but ${DAY[[1, 2, 3, 4, 5, 6, 7].find((x) => !w.includes(x))! - 1]}`;
  return w.map((x) => DAY[x - 1]).join(' ');
};

/** "every day", "Mon Wed Fri", "alternate days (next 5 Nov)". */
export function everyWords(l: Pick<AgreementLine, 'every' | 'weekdays' | 'cycle' | 'nextDelivery'>): string {
  const next = l.nextDelivery ? ` (next ${d(l.nextDelivery)})` : '';
  switch (l.every) {
    case 'DAILY': return 'every day';
    case 'ALTERNATE': return `alternate days${next}`;
    case 'WEEKDAYS': return `${daysWord(l.weekdays)}${next}`;
    default: return `a ${l.cycle?.length ?? ''}-day cycle${next}`;
  }
}

export const spanWords = (from: string, until: string | null): string => {
  if (until === null) return `from ${d(from)}`;
  if (until === from) return d(from);
  const a = new Date(`${from.slice(0, 10)}T00:00:00Z`); const b = new Date(`${until.slice(0, 10)}T00:00:00Z`);
  return a.getUTCMonth() === b.getUTCMonth() ? `${a.getUTCDate()}–${d(until)}` : `${d(from)} – ${d(until)}`;
};

const STATE_WORD: Partial<Record<DayState, string>> = { PREPARED: 'first day prepared', FROZEN: 'first day frozen', LOADED: 'first day loaded', DELIVERED: 'first day delivered' };
export const firstDayWords = (s: DayState): string | null => STATE_WORD[s] ?? null;

/** A run of "skip" over every product of the agreement is one line: Skip all. Pure. */
export interface ExceptionRow { key: string; when: string; words: string; note: string | null; source: AgreementException[] }
export function exceptionRows(list: readonly AgreementException[], now: AgreementCard | null): ExceptionRow[] {
  const rows: ExceptionRow[] = [];
  const pauses = new Map<string, RunException[]>();
  const total = now?.lines.length ?? 0;
  for (const x of list) {
    if (x.kind === 'RUN' && x.effect === 'PAUSE') {
      const k = `${x.from}|${x.until ?? ''}`;
      pauses.set(k, [...(pauses.get(k) ?? []), x]);
      continue;
    }
    if (x.kind === 'RUN') {
      rows.push({ key: `q${x.ids[0]}`, when: spanWords(x.from, x.until), words: `${x.variant} ×${x.quantity}${x.usual ? ` instead of ×${x.usual}` : ''}`, note: firstDayWords(x.firstState), source: [x] });
    } else if (x.kind === 'EXTRA') {
      rows.push({ key: `e${x.orderId}`, when: d(x.from), words: `${x.lines.map((l) => `+${l.variant} ×${l.quantity}`).join(', ')} extra`, note: x.mode === 'ROUND' ? firstDayWords(x.firstState) : x.mode.toLowerCase(), source: [x] });
    } else if (x.kind === 'HOLD') {
      const usual = x.previous.map((l) => `${l.code ?? ''} ×${l.quantity}`).join(', ');
      rows.push({ key: `h${x.id}`, when: `Until notice, from ${d(x.from)}`, words: `held; usual ${usual}`, note: x.reason, source: [x] });
    } else if (x.kind === 'STATUS') {
      rows.push({ key: `s${x.id}`, when: `From ${d(x.from)}${x.until ? ` to ${d(x.until)}` : ''}`, words: x.status === 'LEFT' ? 'leaves' : x.status === 'PAUSED' ? 'paused' : 'active again', note: x.note, source: [x] });
    } else if (x.kind === 'APPROVAL') {
      rows.push({ key: `a${x.changeId}`, when: spanWords(x.from, x.until), words: `${x.change.toLowerCase()} waiting for approval`, note: x.note, source: [x] });
    } else {
      rows.push({ key: `n${x.changeId}${x.from}`, when: d(x.from), words: x.kind === 'NOTE' ? 'note for the rider' : 'to another door', note: x.note, source: [x] });
    }
  }
  for (const [, group] of pauses) {
    const x = group[0]!;
    const all = group.some((g) => g.variant === null) || (total > 0 && new Set(group.map((g) => g.variant)).size >= total);
    rows.push({ key: `p${x.ids[0]}`, when: x.until === null ? `Until notice, from ${d(x.from)}` : spanWords(x.from, x.until), words: all ? 'Skip all' : `Skip ${group.map((g) => g.variant).join(', ')}`, note: firstDayWords(x.firstState), source: group });
  }
  const first = (r: ExceptionRow) => (r.source[0] as { from: string }).from;
  return rows.sort((a, b) => (first(a) < first(b) ? -1 : first(a) > first(b) ? 1 : a.key.localeCompare(b.key)));
}

const WHAT: Record<string, string> = {
  QUANTITY: 'Quantity for the day', SKIP: 'Skip', PAUSE: 'Pause', ADD: 'Add a product', REMOVE: 'Remove a product', SWAP: 'Swap a product', MOVE: 'Move a delivery', MAKEUP: 'Make-up', NOTE: 'Note for the rider', ELSEWHERE: 'Another door',
  NEW_AGREEMENT: 'Agreement changed', FIRST_AGREEMENT: 'First agreement', ACTIVATED: 'Agreement started', SCHEDULE_CHANGED: 'Days changed', DOOR_CHANGED: 'Door changed', PAUSED: 'Agreement paused', RESUMED: 'Resumed', CANCELLED: 'Agreement cancelled', EXPIRED: 'Agreement ended',
  HOLD_STARTED: 'Held until notice', ACTIVE: 'Status: active', LEFT: 'Left', DATED: 'A day changed', REJOINED: 'Rejoined',
};
export const historyWhat = (r: HistoryRow): string => WHAT[r.what] ?? r.what.toLowerCase().replace(/_/g, ' ');
